import { Box, Grid, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Button, StandardDialog, TextField } from '@roc/ui';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useDebtTrackingStore } from '../../hooks/useDebtTrackingStore';
import { formatWarehouseLabel, isNumericLoanId, isValidAmount, parseLoanIds, todayApiDate } from '../../utils/debtTrackingUtils';
import { DebtEntryFields, DebtEntryFormValues } from './debtEntryFields';

const emptyForm = (): DebtEntryFormValues => ({ amount: '', effectiveDate: todayApiDate(), comment: '' });

interface NewDebtEntryDialogProps {
  open: boolean;
  onClose: () => void;
  recordedByName: string;
}

export const NewDebtEntryDialog = observer(({ open, onClose, recordedByName }: NewDebtEntryDialogProps) => {
  const { debtTrackingStore } = useDebtTrackingStore();
  const [loanIdsText, setLoanIdsText] = useState('');
  const [values, setValues] = useState<DebtEntryFormValues>(emptyForm());
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setLoanIdsText('');
      setValues(emptyForm());
      setError('');
    }
  }, [open]);

  const loanIds = parseLoanIds(loanIdsText);
  const canSubmit = loanIds.length > 0 && isValidAmount(values.amount) && !!values.effectiveDate && !isSaving;

  const onFieldChange = (field: keyof DebtEntryFormValues, value: DebtEntryFormValues[keyof DebtEntryFormValues]) => {
    setValues(current => ({ ...current, [field]: value }));
    setError('');
  };

  const submit = async () => {
    const invalidIds = loanIds.filter(loanId => !isNumericLoanId(loanId));
    if (invalidIds.length) {
      setError(`Loan IDs must be numeric: ${invalidIds.join(', ')}`);
      return;
    }
    setIsSaving(true);
    const errorMessage = await flowResult(debtTrackingStore.setFinancingAmount(loanIds, {
      financingAmount: Number(values.amount),
      effectiveDate: values.effectiveDate,
      comment: values.comment.trim(),
    }));
    setIsSaving(false);
    if (errorMessage) setError(errorMessage);
    else onClose();
  };

  return (
    <StandardDialog
      open={open}
      title="New Entry"
      subTitle={formatWarehouseLabel(debtTrackingStore.selectedWarehouse)}
      handleClose={onClose}
      maxWidth="sm"
      removeContentBox
      dialogContent={
        <Box m={2} flexGrow={1} minWidth={0}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                testId="debtEntryLoanIds"
                label="Loan ID(s)"
                required
                variant="outlined"
                fullWidth
                autoFocus
                placeholder="e.g. 85290, 85215"
                helperText="New Loan IDs create a record; existing ones get this as their new financing amount."
                value={loanIdsText}
                onChange={e => { setLoanIdsText(e.target.value); setError(''); }}
              />
            </Grid>
            <Grid item xs={12}>
              <DebtEntryFields values={values} onChange={onFieldChange} recordedByName={recordedByName} />
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Alert severity="error"><Typography variant="body2">{error}</Typography></Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      }
      dialogActions={
        <>
          <Button testId="cancelNewDebtEntry" variant="outlined" color="primary" onClick={onClose}>
            Cancel
          </Button>
          <Button testId="submitNewDebtEntry" variant="contained" color="primary" disabled={!canSubmit} onClick={submit}>
            {loanIds.length > 1 ? `Save ${loanIds.length} Loans` : 'Save'}
          </Button>
        </>
      }
    />
  );
});
